/**
 * Draws one frame of a venture dot canvas: each dot breathes and wobbles,
 * is projected to the canvas, is pushed away from the pointer (eased, so it
 * drifts back when the pointer leaves), and is sized and faded by depth.
 */

/**
 * The time-varying wobble of one dot, added to its rest position.
 * @returns {[number, number, number]} zeros when not animating
 */
export const wobble = (p, t, animate) =>
	animate
		? [
				Math.sin(t * 0.0022 + p.phase) * p.jx,
				Math.cos(t * 0.0022 + p.phase * 1.3) * p.jy,
				Math.sin(t * 0.002 + p.phase * 0.7) * p.jz,
			]
		: [0, 0, 0];

/**
 * How far the pointer pushes a dot at (x, y): strongest next to it, nothing
 * beyond `radius`, quadratic in between.
 * @param {{ hovering: boolean, mx: number, my: number }} pointer
 * @returns {[number, number]}
 */
export const pointerPush = (x, y, pointer, radius, strength) => {
	if (!pointer.hovering) return [0, 0];
	const dx = x - pointer.mx;
	const dy = y - pointer.my;
	const d2 = dx * dx + dy * dy;
	if (d2 >= radius * radius || d2 <= 0.01) return [0, 0];
	const d = Math.sqrt(d2);
	const f = 1 - d / radius;
	const fs = f * f * strength;
	return [(dx / d) * fs, (dy / d) * fs];
};

/**
 * Renders every dot in `state.dots` for time t.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ w: number, h: number, dots: any[], hovering: boolean, mx: number, my: number }} state
 * @param {number} t rAF timestamp in ms
 * @param {boolean} animate false draws the rest pose (reduced motion)
 * @param {{ color: string, radiusScale: number, breathAmp: number, pushScale: number,
 *   project?: (xyz: number[]) => number[] }} look `project` maps the wobbled
 *   unit-space point to the view (e.g. a rotation); identity by default.
 */
export function drawDotField(ctx, state, t, animate, look) {
	const { w, h, dots } = state;
	const { color, radiusScale, breathAmp, pushScale, project = (xyz) => xyz } = look;
	const min = Math.min(w, h);
	const radius = min * radiusScale;
	ctx.clearRect(0, 0, w, h);
	ctx.fillStyle = color;
	for (const p of dots) {
		const breath = animate ? 1 + Math.sin(t * 0.0024 * p.freq + p.phase) * breathAmp : 1;
		const [wx, wy, wz] = wobble(p, t, animate);
		const [x, y, z] = project([p.x + wx, p.y + wy, p.z + wz]);
		const baseX = w / 2 + x * radius * breath;
		const baseY = h / 2 + y * radius * breath;
		const [tx, ty] = pointerPush(baseX, baseY, state, min * 0.28, min * pushScale);
		p.ox += (tx - p.ox) * 0.12;
		p.oy += (ty - p.oy) * 0.12;
		const depth = (z + 1) * 0.5;
		ctx.globalAlpha = 0.18 + depth * 0.72;
		ctx.beginPath();
		ctx.arc(baseX + p.ox, baseY + p.oy, 0.7 + depth * 1.9, 0, Math.PI * 2);
		ctx.fill();
	}
	ctx.globalAlpha = 1;
}
